import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { StyledModal } from 'components/shared';
import { useCountryValue, selectCountry } from 'stores/country';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Button } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import cx from 'classnames';
import getNearestCountry from 'utils/getNearestCountry';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { isValidCountry, getCountryAppCode } from 'utils/geolocation';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { getCountryForLocale, parseLocalePath } from 'lib/lang-utils';

const ModalContent = styled.div`
  width: 20.625rem;
  border-radius: 0.625rem;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.4);
  @media ${breakpoints.md} {
    width: 36.25rem;
  }
`;

const Heading = styled.div`
  max-width: 15rem;
  padding-top: 2.85rem;
  letter-spacing: -0.5px;
  line-height: 2.625rem;
  @media ${breakpoints.md} {
    max-width: 26rem;
    padding-top: 4.8rem;
  }
`;

const Text = styled.div`
  max-width: 16.938rem;
  letter-spacing: 0.11px;
  @media ${breakpoints.md} {
    max-width: 29.938rem;
  }
`;
const ButtonsContainer = styled.div`
  padding-bottom: 1.75rem;
  @media ${breakpoints.md} {
    margin-top: 1.45rem;
    padding-bottom: 2.75rem;
  }
`;
const CountryButton = styled(Button)`
  @media ${breakpoints.md} {
    width: 10rem;
  }
`;

const CountrySelector = ({ fields, sitecoreContext }) => {
  const titleCountrySelector = getFieldValue(fields, 'titleCountrySelector');
  const descriptionCountrySelector = getFieldValue(
    fields,
    'descriptionCountrySelector',
  );
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const [{ country }, dispatch] = useCountryValue();
  const location = useLocation();
  const navigate = useNavigate();
  const params = queryString.parse(location.search);
  const [isResolvingCountry, setIsResolvingCountry] = useState(true);

  const handleClick = useCallback((value) => {
    if (typeof window !== 'undefined' && localStorage) {
      localStorage.setItem('userCountry', value);
      dispatch(selectCountry(value));
    }
  });

  const geolocation = getFieldValue(sitecoreContext, 'geolocation') || {};
  // Pulled out as primitives so the effect below can list exhaustive deps
  // without depending on `params`/`geolocation`, which are new objects on every
  // render and would make the effect re-run forever.
  const countryParam = params.country;
  const geoCountry = geolocation.country;
  const geoLatitude = geolocation.latitude;
  const geoLongitude = geolocation.longitude;

  // A locale in the path is authoritative for the country, and AppRoot has
  // already seeded the country context from it. Inferring from localStorage or
  // geolocation as well would fight that seeding and ping-pong the two values
  // against each other, so on a localized route the locale simply wins and the
  // inference below is skipped entirely.
  const localeCountry = getCountryForLocale(
    parseLocalePath(location.pathname).locale,
  );

  // Resolve the persisted / geolocated country AFTER mount. Previously this ran
  // during render, which (a) read localStorage so the server and the client's
  // first render disagreed (React #418) and (b) dispatched mid-render, which
  // React does not allow. `country` from CountryContext is the only source the
  // render below reads, so hydration now matches.
  useEffect(() => {
    let resolved = localeCountry;

    try {
      if (localeCountry) {
        // The locale already decided the country; only persist it so the rest
        // of the app (RouteHandler, the country store) stays in sync.
        localStorage.setItem('userCountry', localeCountry);
      } else {
        resolved = localStorage.getItem('userCountry');

        // An explicit ?country= param is the source of truth, and is persisted
        // so RouteHandler and the country store stay in sync.
        if (countryParam && ['CAN', 'USA'].includes(countryParam)) {
          resolved = countryParam;
          localStorage.setItem('userCountry', resolved);
        }

        if (!resolved && geoCountry) {
          resolved = isValidCountry(geoCountry)
            ? getCountryAppCode(geoCountry)
            : getNearestCountry(geoLatitude, geoLongitude);

          if (resolved) {
            localStorage.setItem('userCountry', resolved);
          }
        }
      }
    } catch {
      // Storage blocked (e.g. private mode): leave the country unset so the
      // selector modal is shown instead.
      //console.log('Show modal when there is no location available');
    }

    if (resolved && resolved !== country) {
      dispatch(selectCountry(resolved));
    }
    setIsResolvingCountry(false);
  }, [
    localeCountry,
    countryParam,
    geoCountry,
    geoLatitude,
    geoLongitude,
    country,
    dispatch,
  ]);

  const titleCountrySelectorRef = useRef();

  useEffect(() => {
    setTimeout(() => {
      if (titleCountrySelectorRef.current) {
        const element = titleCountrySelectorRef.current;
        element.children[0].focus();
      }
    }, 2000);
  }, [titleCountrySelectorRef]);

  if (isResolvingCountry) {
    //console.log('isResolvingCountry true - waiting for country param update');
    return null;
  }
  return ['CAN', 'USA'].includes(country) ? (
    ''
  ) : (
    <StyledModal isOpen={!['USA', 'CAN'].includes(country)}>
      <div
        className='flex justify-center items-center bg-black-faded w-screen h-screen'
        aria-modal='true'
        role='dialog'
      >
        <ModalContent className='flex flex-col items-center bg-white'>
          <Heading className='w-full font-trade-gothic-20 text-mattamy-blue text-4xl text-center'>
            {titleCountrySelector}
          </Heading>

          <Text className='w-full font-graphie text-mattamy-blue text-base text-center leading-7 mt-3 md:mt-0'>
            {descriptionCountrySelector}
          </Text>
          <ButtonsContainer
            className={cx('items-center', {
              'flex flex-col mt-8 w-full px-6': isMobile,
            })}
            ref={titleCountrySelectorRef}
          >
            <CountryButton
              color={'action-blue'}
              variant={'outlined'}
              className='text-base h-60-mattamy pt-0 pb-0 mb-5 md:mr-2 md:ml-3 hover:text-white hover:bg-action-blue'
              onClick={() => handleClick('CAN')}
              tabIndex={0}
            >
              CANADA
            </CountryButton>
            <CountryButton
              color={'action-blue'}
              variant={'outlined'}
              className='text-base h-60-mattamy pt-0 pb-0 mb-5 md:mr-2 md:ml-3 hover:text-white hover:bg-action-blue'
              onClick={() => handleClick('USA')}
              tabIndex={0}
            >
              USA
            </CountryButton>
          </ButtonsContainer>
        </ModalContent>
      </div>
    </StyledModal>
  );
};

export default withSitecoreContext({ updatable: true })(CountrySelector);
